import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import type { User } from '@opendoor/auth-fe/context';

import { useObservability } from '../../../helpers/observability';
import { DEPLOY_ENV } from '../../globals';
import { getAnonymousId } from '../../shared/trackers';
import {
  determinePropertyActionability,
  type PropertyActionabilityInput,
  type TourKind,
} from './propertyActionability';

interface EventData {
  [key: string]: unknown;
}

interface TrackProperty {
  addressToken?: string;
  city?: string;
  state?: string;
  zip?: string;
  listingState?: string;
  canSelfTour?: boolean;
  hasKnownCurrentAvailability?: boolean;
  isActionable?: boolean;
  showBuyDirect?: boolean;
  disableBuyDirect?: boolean;
  tourKind?: TourKind | 'none';
  propertyAnalyticsSurface?: PropertyAnalyticsSurface;
  slug?: string;
}

type TrackableProperty = {
  addressToken?: string | null;
  canSelfTour?: boolean | null;
  city?: string | null;
  hasKnownCurrentAvailability?: boolean | null;
  listingState?: string | null;
  slug?: string | null;
  state?: string | null;
  zip?: string | null;
};

export type TrackPropertyOptions = {
  addressToken: string;
};

type BuildPayloadOptions<T extends EventData> = {
  property?: TrackableProperty;
  additionalContextualData?: T;
  user?: User | null;
  eligibility?: PropertyEligibility;
  surface?: PropertyAnalyticsSurface;
};

type TrackPropertyArguments<T extends EventData> = {
  featureName: PropertyEventAction;
  label?: string;
  additionalContextualData?: T;
  options?: TrackPropertyOptions;
};

export type PropertyEligibility = ReturnType<typeof determinePropertyActionability>;

export type PropertyAnalyticsSurface = 'map-drawer' | 'pdp';

// Action names should follow this format
type PropertyEventAction =
  | 'property-view'
  | 'property-favorite'
  | 'property-share'
  | 'property-self-tour'
  | 'property-tour-with-agent'
  | 'property-buy-direct';

interface PropertyAnalyticsProps {
  property?: TrackableProperty;
  eligibility?: PropertyEligibility;
  surface?: PropertyAnalyticsSurface;
}

const initialPropertyContext: PropertyAnalyticsProps = {
  property: undefined,
  eligibility: undefined,
  surface: undefined,
};

export const PropertyAnalytics = createContext(initialPropertyContext);

const usePropertyAnalytics = () => useContext(PropertyAnalytics);

export const buildPropertyAnalyticsValue = (
  property: TrackableProperty & PropertyActionabilityInput,
  surface: PropertyAnalyticsSurface = 'pdp',
) => ({
  property,
  eligibility: determinePropertyActionability(property),
  surface,
});

export function buildPayload<T extends EventData = EventData>({
  property,
  additionalContextualData,
  user,
  eligibility,
  surface,
}: BuildPayloadOptions<T> = {}) {
  const trackPropertyFields: TrackProperty = {
    ...(property?.city && { city: property?.city }),
    ...(property?.state && { state: property?.state }),
    ...(property?.zip && { zip: property?.zip }),
    ...(property?.slug && { slug: property?.slug }),
    ...(property?.addressToken && { addressToken: property?.addressToken }),
    ...(property?.listingState && { listingState: property?.listingState }),
    ...(property?.canSelfTour != null &&
      property.hasKnownCurrentAvailability !== false && { canSelfTour: property.canSelfTour }),
    ...(property?.hasKnownCurrentAvailability != null && {
      hasKnownCurrentAvailability: property.hasKnownCurrentAvailability,
    }),
    ...(eligibility && {
      isActionable: eligibility.isActionable,
      showBuyDirect: eligibility.showBuyDirect,
      disableBuyDirect: eligibility.disableBuyDirect,
      tourKind: eligibility.tourKind ?? 'none',
    }),
    ...(surface && { propertyAnalyticsSurface: surface }),
  };

  const properties = {
    ...additionalContextualData,
    ...trackPropertyFields,
    anonymousId: getAnonymousId(),
    ...(user?.customerUuid && { customerId: user.customerUuid }),
  };
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value != null && value !== ''),
  );
}

export const useTrackProperty = () => {
  // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
  const { trackEvent } = useObservability();
  const { property, eligibility, surface } = usePropertyAnalytics();
  const { user } = useAuth();
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  const trackProperty = useCallback(
    function <T extends EventData = EventData>({
      featureName,
      label,
      additionalContextualData,
      options,
    }: TrackPropertyArguments<T>) {
      const isContextProperty = options == null || options.addressToken === property?.addressToken;
      const payload = buildPayload({
        property: isContextProperty ? property : { addressToken: options.addressToken },
        additionalContextualData,
        user: userRef.current,
        eligibility: isContextProperty ? eligibility : undefined,
        surface,
      });
      trackEvent(featureName, label, payload);

      if (DEPLOY_ENV === 'development') {
        console.debug(`[Analytics]`, { featureName, label, payload });
      }
    },
    [trackEvent, property, eligibility, surface],
  );

  return { trackProperty };
};
